import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { GripVertical, Calendar, Sun, Moon, Pencil, Trash2, ChevronDown, ChevronRight, X, FastForward, Triangle } from 'lucide-react';
import { format } from 'date-fns';
import BubbleCheckButton from './BubbleCheckButton';
import EditTaskDialog from './EditTaskDialog';
import { useSettings } from '@/contexts/SettingsContext';
import axios from 'axios';
import { toast } from 'sonner';
import { parseLocalDate } from '@/utils/dateHelpers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TaskCard({ task, onComplete, onUpdate, showDragHandle = false, isDragging = false, tags = [], dragHandlePosition = 'right', onMoveToTop, onMoveToBottom, onMoveUp, onMoveDown, compact = false }) {
  const { assigneeTagsEnabled, energyTagsEnabled, activeLists, taskCardTagsDisplay } = useSettings();
  const [showEdit, setShowEdit] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [subtasksExpanded, setSubtasksExpanded] = useState(false);
  const [showActiveDatePicker, setShowActiveDatePicker] = useState(false);
  const [showDueDatePicker, setShowDueDatePicker] = useState(false);
  const [showListPicker, setShowListPicker] = useState(false);
  const hasMultipleLists = (activeLists?.length || 0) > 1;
  const isOverdue = task.due_date && parseLocalDate(task.due_date) < new Date() && !task.completed;
  const isDueSoon =
    task.due_date &&
    !isOverdue &&
    parseLocalDate(task.due_date) < new Date(Date.now() + 3 * 24 * 60 * 60 * 1000);

  const activateWithList = async (chosenListId) => {
    if (isUpdating) return;
    setIsUpdating(true);
    setShowListPicker(false);
    try {
      const updateData = {
        is_active: true,
        manually_deactivated: false,
        activation_date: null,
      };
      if (chosenListId) updateData.active_list_id = chosenListId;
      await axios.patch(`${API}/tasks/${task.id}`, updateData, { withCredentials: true });
      toast.success('Marked as Active', { duration: 3000 });
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error activating task:', error);
      toast.error('Failed to update task');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleToggleActive = async () => {
    if (isUpdating) return;
    setIsUpdating(true);
    try {
      const updateData = {
        is_active: !task.is_active,
      };
      
      // If marking as inactive, set manually_deactivated flag and clear activation_date
      if (task.is_active) {
        updateData.manually_deactivated = true;
        updateData.activation_date = null;
      } else {
        // If manually activating, clear the manually_deactivated flag and clear activation_date
        updateData.manually_deactivated = false;
        updateData.activation_date = null;
      }
      
      await axios.patch(`${API}/tasks/${task.id}`, updateData, { withCredentials: true });
      toast.success(task.is_active ? 'Marked as Inactive' : 'Marked as Active', { duration: 3000 });
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error toggling active:', error);
      toast.error('Failed to update task');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleToggleSubtask = async (subtaskId) => {
    try {
      const updatedSubtasks = task.subtasks.map(st =>
        st.id === subtaskId ? { ...st, completed: !st.completed } : st
      );
      await axios.patch(`${API}/tasks/${task.id}`, {
        subtasks: updatedSubtasks,
      }, { withCredentials: true });
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error toggling subtask:', error);
      toast.error('Failed to update subtask');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/tasks/${task.id}`, { withCredentials: true });
      toast.success('Task deleted', { duration: 3000 });
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error('Failed to delete task');
    }
  };

  const handleTagChange = async (tagType, newValue) => {
    try {
      const updateData = {};
      if (tagType === 'type') updateData.tag_type = newValue;
      if (tagType === 'domain') updateData.tag_domain = newValue;
      if (tagType === 'energy') updateData.tag_energy = newValue;
      if (tagType === 'assignee') updateData.assignee_tag = newValue;
      
      await axios.patch(`${API}/tasks/${task.id}`, updateData, {
        withCredentials: true
      });
      toast.success('Tag updated', { duration: 3000 });
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error updating tag:', error);
      toast.error('Failed to update tag');
    }
  };

  const handleDateChange = async (dateType, newDate) => {
    try {
      const updateData = {};
      if (dateType === 'active') {
        updateData.activation_date = newDate ? format(newDate, 'yyyy-MM-dd') : null;
      } else if (dateType === 'due') {
        updateData.due_date = newDate ? format(newDate, 'yyyy-MM-dd') : null;
      }
      
      await axios.patch(`${API}/tasks/${task.id}`, updateData, {
        withCredentials: true
      });
      
      if (dateType === 'active') {
        setShowActiveDatePicker(false);
        toast.success(newDate ? 'Active date updated' : 'Active date cleared', { duration: 3000 });
      } else {
        setShowDueDatePicker(false);
        toast.success(newDate ? 'Due date updated' : 'Due date cleared', { duration: 3000 });
      }
      
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error updating date:', error);
      toast.error('Failed to update date');
    }
  };

  // Filter and sort tags - Type, Domain, Energy tags are filtered by task's context
  // Assignee tags are global (no context filtering)
  const taskContextId = task.context_id;
  const typeTags = tags.filter((t) => t.category === 'Type' && (!taskContextId || t.context_id === taskContextId)).sort((a, b) => a.priority - b.priority);
  const domainTags = tags.filter((t) => t.category === 'Domain' && (!taskContextId || t.context_id === taskContextId)).sort((a, b) => a.priority - b.priority);
  const energyTags = tags.filter((t) => t.category === 'Energy' && (!taskContextId || t.context_id === taskContextId)).sort((a, b) => a.priority - b.priority);
  const assigneeTags = tags.filter((t) => t.category === 'assignee').sort((a, b) => a.priority - b.priority);

  const topArrowBtn = onMoveToTop && (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onMoveToTop(task.id);
      }}
      className="text-muted-foreground hover:text-foreground transition-colors no-hover"
      title="Move to top"
    >
      <div className="flex flex-col items-center">
        <Triangle size={14} className="-mb-1.5" />
        <Triangle size={14} />
      </div>
    </button>
  );
  const bottomArrowBtn = onMoveToBottom && (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onMoveToBottom(task.id);
      }}
      className="text-muted-foreground hover:text-foreground transition-colors no-hover"
      title="Move to bottom"
    >
      <div className="flex flex-col items-center" style={{ transform: 'rotate(180deg)' }}>
        <Triangle size={14} className="-mb-1.5" />
        <Triangle size={14} />
      </div>
    </button>
  );
  const gripHandle = (
    <div className="cursor-grab active:cursor-grabbing flex flex-col gap-0">
      <GripVertical size={20} className="text-muted-foreground -mb-2" />
      <GripVertical size={20} className="text-muted-foreground" />
    </div>
  );
  const isIconOnly = taskCardTagsDisplay === 'icon_only';

  return (
    <>
      <div
        className={`bg-card text-card-foreground rounded-2xl border border-[hsl(var(--border))] shadow-[0_8px_24px_rgba(0,0,0,0.06)] ${compact ? 'px-3 py-2' : 'px-6 py-3 md:py-4'} hover:shadow-[0_12px_28px_rgba(0,0,0,0.08)] transition-shadow ${
          isOverdue ? 'ring-2 ring-[hsl(var(--destructive))]/40' : ''
        } ${isDueSoon ? 'ring-1 ring-[hsl(var(--warning))]/40' : ''} ${
          isDragging ? 'opacity-50' : ''
        }`}
        data-testid="task-card"
      >
        <div className="flex items-center gap-3">
          {showDragHandle && dragHandlePosition === 'left' && (
            <>
              {/* Desktop: drag handle with double arrows. In icon_only mode, arrows split out to sit beside the handle, toward the content/buttons */}
              {isIconOnly ? (
                <div className="hidden md:flex items-center self-center gap-2" data-testid="task-drag-handle">
                  <div className="flex flex-col items-center justify-center">{gripHandle}</div>
                  <div className="flex flex-col items-center justify-center gap-4">
                    {topArrowBtn}
                    {bottomArrowBtn}
                  </div>
                </div>
              ) : (
                <div className="hidden md:flex flex-col items-center justify-center self-center gap-1.5" data-testid="task-drag-handle">
                  {topArrowBtn}
                  {gripHandle}
                  {bottomArrowBtn}
                </div>
              )}
              
              {/* Mobile: All four arrows (double + single) */}
              <div className="md:hidden flex flex-col items-center justify-center self-center gap-1.5">
                {onMoveToTop && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onMoveToTop(task.id);
                    }}
                    className="text-muted-foreground hover:text-foreground transition-colors p-1 no-hover"
                    title="Move to top"
                  >
                    <div className="flex flex-col items-center">
                      <Triangle size={14} className="-mb-1.5" />
                      <Triangle size={14} />
                    </div>
                  </button>
                )}
                {onMoveUp && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onMoveUp(task.id);
                    }}
                    className="text-muted-foreground hover:text-foreground transition-colors p-1 no-hover"
                    title="Move up"
                  >
                    <Triangle size={14} />
                  </button>
                )}
                {onMoveDown && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onMoveDown(task.id);
                    }}
                    className="text-muted-foreground hover:text-foreground transition-colors p-1 no-hover"
                    title="Move down"
                  >
                    <Triangle size={14} style={{ transform: 'rotate(180deg)' }} />
                  </button>
                )}
                {onMoveToBottom && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onMoveToBottom(task.id);
                    }}
                    className="text-muted-foreground hover:text-foreground transition-colors p-1 no-hover"
                    title="Move to bottom"
                  >
                    <div className="flex flex-col items-center" style={{ transform: 'rotate(180deg)' }}>
                      <Triangle size={14} className="-mb-1.5" />
                      <Triangle size={14} />
                    </div>
                  </button>
                )}
              </div>
            </>
          )}
          <div className="flex-shrink-0">
            <BubbleCheckButton task={task} onComplete={onComplete} />
          </div>
          <div className="flex-1 min-w-0 flex items-center justify-between gap-3">
            <div className="flex-1 min-w-0">
              <h3 className={`font-medium leading-snug ${compact ? 'text-sm' : 'text-sm md:text-base'}`} data-testid="task-title">
                {task.title}
              </h3>
              
              {/* Tags - clickable to change */}
              <div className="flex flex-wrap gap-1.5 mt-2" data-testid="task-tags">
                {assigneeTagsEnabled && task.assignee_tag && (
                  <Select value={task.assignee_tag} onValueChange={(value) => handleTagChange('assignee', value)}>
                    <SelectTrigger className="h-6 w-auto border-0 px-2 py-0 text-xs hover:text-foreground transition-colors" style={{ backgroundColor: 'var(--color-secondary)', color: 'black' }}>
                      <SelectValue>{taskCardTagsDisplay === 'icon_only' ? task.assignee_tag.split(' ')[0] : task.assignee_tag}</SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {assigneeTags.map((tag) => (
                        <SelectItem key={tag.id} value={`${tag.emoji} ${tag.name}`}>
                          {tag.emoji} {tag.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {task.tag_type && (
                  <Select value={task.tag_type} onValueChange={(value) => handleTagChange('type', value)}>
                    <SelectTrigger className="h-6 w-auto border-0 px-2 py-0 text-xs hover:text-foreground transition-colors" style={{ backgroundColor: 'var(--color-secondary)', color: 'black' }}>
                      <SelectValue>{taskCardTagsDisplay === 'icon_only' ? task.tag_type.split(' ')[0] : task.tag_type}</SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {typeTags.map((tag) => (
                        <SelectItem key={tag.id} value={`${tag.emoji} ${tag.name}`}>
                          {tag.emoji} {tag.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {task.tag_domain && (
                  <Select value={task.tag_domain} onValueChange={(value) => handleTagChange('domain', value)}>
                    <SelectTrigger className="h-6 w-auto border-0 px-2 py-0 text-xs hover:text-foreground transition-colors" style={{ backgroundColor: 'var(--color-secondary)', color: 'black' }}>
                      <SelectValue>{taskCardTagsDisplay === 'icon_only' ? task.tag_domain.split(' ')[0] : task.tag_domain}</SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {domainTags.map((tag) => (
                        <SelectItem key={tag.id} value={`${tag.emoji} ${tag.name}`}>
                          {tag.emoji} {tag.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {energyTagsEnabled && task.tag_energy && (
                  <Select value={task.tag_energy} onValueChange={(value) => handleTagChange('energy', value)}>
                    <SelectTrigger className="h-6 w-auto border-0 px-2 py-0 text-xs hover:text-foreground transition-colors" style={{ backgroundColor: 'var(--color-secondary)', color: 'black' }}>
                      <SelectValue>{taskCardTagsDisplay === 'icon_only' ? task.tag_energy.split(' ')[0] : task.tag_energy}</SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {energyTags.map((tag) => (
                        <SelectItem key={tag.id} value={`${tag.emoji} ${tag.name}`}>
                          {tag.emoji} {tag.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
              
              {/* Subtasks below tags - all collapsible */}
              {task.subtasks && task.subtasks.length > 0 && (
                <div className="mt-2">
                  <button
                    onClick={() => setSubtasksExpanded(!subtasksExpanded)}
                    className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors no-hover"
                  >
                    {subtasksExpanded ? (
                      <ChevronDown size={14} />
                    ) : (
                      <ChevronRight size={14} />
                    )}
                    <span className="font-medium">Subtasks ({task.subtasks.filter(st => !st.completed).length})</span>
                  </button>
                  {subtasksExpanded && (
                    <div className="mt-1 space-y-1 pl-5" data-testid="task-subtasks">
                      {task.subtasks.map((subtask) => (
                        <div key={subtask.id} className="flex items-start gap-2 text-xs">
                          <input
                            type="checkbox"
                            checked={subtask.completed}
                            onChange={() => handleToggleSubtask(subtask.id)}
                            className="h-3 w-3 mt-0.5 rounded border-gray-300 flex-shrink-0"
                          />
                          <span className={subtask.completed ? "line-through text-muted-foreground" : "text-muted-foreground"}>
                            {subtask.title}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              {task.notes && (
                <p className="text-xs md:text-sm text-muted-foreground mt-2 line-clamp-2">
                  {task.notes}
                </p>
              )}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-muted-foreground">
                {/* Active Date - Always shown, clickable */}
                <Popover open={showActiveDatePicker} onOpenChange={setShowActiveDatePicker}>
                  <PopoverTrigger asChild>
                    <div 
                      className="flex items-center gap-1 cursor-pointer hover:text-foreground transition-colors" 
                      data-testid="task-active-date"
                    >
                      <Calendar size={14} />
                      <span>
                        Active: {task.is_active && !task.activation_date 
                          ? 'Now'
                          : task.activation_date && parseLocalDate(task.activation_date) 
                          ? format(parseLocalDate(task.activation_date), 'MM/dd/yyyy')
                          : 'No Date'}
                      </span>
                    </div>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <div className="p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium">Active Date</span>
                        {task.activation_date && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDateChange('active', null)}
                            className="h-6 px-2 text-xs"
                          >
                            <X size={14} className="mr-1" />
                            Clear
                          </Button>
                        )}
                      </div>
                      <CalendarComponent
                        mode="single"
                        selected={task.activation_date ? parseLocalDate(task.activation_date) : undefined}
                        onSelect={(date) => handleDateChange('active', date)}
                        initialFocus
                      />
                    </div>
                  </PopoverContent>
                </Popover>

                {/* Due Date - floats naturally to the right of Active Date */}
                <Popover open={showDueDatePicker} onOpenChange={setShowDueDatePicker}>
                  <PopoverTrigger asChild>
                    <div 
                      className="flex items-center gap-1 cursor-pointer hover:text-foreground transition-colors" 
                      data-testid="task-due-date"
                    >
                      <Calendar size={14} />
                      <span>
                        Due: {task.due_date && parseLocalDate(task.due_date) 
                          ? format(parseLocalDate(task.due_date), 'MM/dd/yyyy')
                          : 'No Date'}
                      </span>
                    </div>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <div className="p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium">Due Date</span>
                        {task.due_date && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDateChange('due', null)}
                            className="h-6 px-2 text-xs"
                          >
                            <X size={14} className="mr-1" />
                            Clear
                          </Button>
                        )}
                      </div>
                      <CalendarComponent
                        mode="single"
                        selected={task.due_date ? parseLocalDate(task.due_date) : undefined}
                        onSelect={(date) => handleDateChange('due', date)}
                        initialFocus
                      />
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            <div className={`flex flex-col gap-1 flex-shrink-0 self-center ${compact ? 'scale-90' : ''}`}>
              {!task.is_active && hasMultipleLists ? (
                <Popover open={showListPicker} onOpenChange={setShowListPicker}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className={compact ? "h-6 w-6" : "h-8 w-8"}
                      disabled={isUpdating}
                      data-testid="toggle-active-button"
                      title="Activate task — pick a list"
                    >
                      <Moon size={compact ? 14 : 18} className="text-gray-400" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-56 p-2"
                    align="end"
                    onClick={(e) => e.stopPropagation()}
                    data-testid="activate-list-picker"
                  >
                    <div className="text-xs text-muted-foreground px-2 py-1">
                      Activate to which list?
                    </div>
                    <div className="space-y-1 mt-1">
                      {activeLists.map((l) => (
                        <button
                          key={l.id}
                          type="button"
                          onClick={() => activateWithList(l.id)}
                          className="w-full text-left px-2 py-1.5 text-sm rounded hover:bg-accent hover:text-black transition-colors"
                          data-testid={`activate-to-list-${l.id}`}
                        >
                          {l.name}
                        </button>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
              ) : (
                <Button
                  variant="ghost"
                  size="icon"
                  className={compact ? "h-6 w-6" : "h-8 w-8"}
                  onClick={handleToggleActive}
                  disabled={isUpdating}
                  data-testid="toggle-active-button"
                  title={task.is_active ? 'Mark as Inactive' : 'Mark as Active'}
                >
                  {task.is_active ? (
                    <Sun size={compact ? 14 : 18} className="text-yellow-600" />
                  ) : (
                    <Moon size={compact ? 14 : 18} className="text-gray-400" />
                  )}
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                className={compact ? "h-6 w-6" : "h-8 w-8"}
                onClick={() => setShowEdit(true)}
                data-testid="edit-task-button"
                title="Edit task"
              >
                <Pencil size={compact ? 12 : 16} className="text-blue-500" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className={compact ? "h-6 w-6" : "h-8 w-8"}
                onClick={() => setShowDeleteConfirm(true)}
                data-testid="delete-task-button"
                title="Delete task"
              >
                <Trash2 size={compact ? 12 : 16} className="text-red-500" />
              </Button>
            </div>
          </div>
          {showDragHandle && dragHandlePosition === 'right' && (
            <>
              {/* Desktop: drag handle with double arrows. In icon_only mode, arrows split out to sit beside the handle, toward the buttons */}
              {isIconOnly ? (
                <div className="hidden md:flex items-center self-center gap-2" data-testid="task-drag-handle">
                  <div className="flex flex-col items-center justify-center gap-4">
                    {topArrowBtn}
                    {bottomArrowBtn}
                  </div>
                  <div className="flex flex-col items-center justify-center">{gripHandle}</div>
                </div>
              ) : (
                <div className="hidden md:flex flex-col items-center justify-center self-center gap-1.5" data-testid="task-drag-handle">
                  {topArrowBtn}
                  {gripHandle}
                  {bottomArrowBtn}
                </div>
              )}
              
              {/* Mobile: All four arrows (double + single) */}
              <div className="md:hidden flex flex-col items-center justify-center self-center gap-1.5">
                {onMoveToTop && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onMoveToTop(task.id);
                    }}
                    className="text-muted-foreground hover:text-foreground transition-colors p-1 no-hover"
                    title="Move to top"
                  >
                    <div className="flex flex-col items-center">
                      <Triangle size={14} className="-mb-1.5" />
                      <Triangle size={14} />
                    </div>
                  </button>
                )}
                {onMoveUp && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onMoveUp(task.id);
                    }}
                    className="text-muted-foreground hover:text-foreground transition-colors p-1 no-hover"
                    title="Move up"
                  >
                    <Triangle size={14} />
                  </button>
                )}
                {onMoveDown && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onMoveDown(task.id);
                    }}
                    className="text-muted-foreground hover:text-foreground transition-colors p-1 no-hover"
                    title="Move down"
                  >
                    <Triangle size={14} style={{ transform: 'rotate(180deg)' }} />
                  </button>
                )}
                {onMoveToBottom && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onMoveToBottom(task.id);
                    }}
                    className="text-muted-foreground hover:text-foreground transition-colors p-1 no-hover"
                    title="Move to bottom"
                  >
                    <div className="flex flex-col items-center" style={{ transform: 'rotate(180deg)' }}>
                      <Triangle size={14} className="-mb-1.5" />
                      <Triangle size={14} />
                    </div>
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <EditTaskDialog
        open={showEdit}
        onOpenChange={setShowEdit}
        task={task}
        onTaskUpdated={onUpdate}
      />

      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent data-testid="delete-confirm-dialog" className="max-w-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Task</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-3 sm:space-x-0">
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600 hover:text-black"
              data-testid="confirm-delete-button"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
